import React, { useState } from "react";
import { Link, Link as RouterLink } from "react-router-dom";
import { IconButton, makeStyles, Typography } from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableContainer from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import { Delete, Edit } from "@material-ui/icons";
import styled from "styled-components";
import {
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useTable,
  useSortBy,
} from "react-table";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import apiHelper from "../../../../util/ApiHelper";
import { CustomCheckbox, formatDatetime, modal } from "../../../../util";
import { useLoading } from "../../../../util/LoadingProvider";
import { toast } from "react-toastify";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { PaginationHandler } from "../../../../util/useManualPagination";
import PaginationBarForHandler from "../../../common/PaginationBarForHandler";

const useStyles = makeStyles((theme) => ({
  textInput: {
    backgroundColor: "white",
    width: "100%",
    marginBottom: "12px",

    "& input": {
      fontSize: "14px",
    },
  },
  content: {
    "& .ck-editor__editable_inline": {
      minHeight: "200px",
    },
  },
  text: {
    textDecoration: "none",
    color: "#306F9D",
  },
  inactive: {
    color: "#999999",
  },
  buttonGroup: {
    marginTop: "20px",
    textAlign: "right",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  box: {
    padding: 0,
    background: "#ffffff",
    paddingTop: "10px",
    paddingBottom: "10px",
    minHeight: "800px",
  },
  innerBox: {
    background: "#ffffff",
    padding: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  button: {
    backgroundColor: "#306F9D",
    textTransform: "capitalize",
    color: "white",
    "&:hover": {
      backgroundColor: "#306F9D",
    },
  },
  pagination: {
    "& .MuiTextField-root": {
      width: "35px",
      minWidth: "auto",
    },
    "& .MuiSelect-root": {
      paddingTop: "3px",
      paddingBottom: "3px",
    },
    "& input": {
      padding: "4px 0",
      marginLeft: "10px",
      marginRight: "10px",
    },
    "& button": {
      background: "white",
      color: "#cccccc",
      border: "1px solid #CCCCCC",
      minWidth: "auto",
      width: "24px",
      height: "24px",
      margin: "4px 0",
      "&.left": {
        borderRadius: "4px 0 0 4px",
        marginLeft: "10px",
      },
      "&.right": {
        borderRadius: " 0 4px 4px 0",
      },
    },
  },
  displayAllBtn: {
    textDecoration: "underline",
    color: "#306f9d",
    paddingRight: "10px",
    "&:hover": {
      cursor: "pointer",
    },
  },
  sortableHeader: {
    cursor: "pointer",
  },
}));

/** DRAG N DROP PREPARATION STARTS **/

const IsDraggingContext = React.createContext(false);

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

class DraggableTableCell extends React.Component {
  getSnapshotBeforeUpdate(prevProps) {
    if (!this.ref) {
      return null;
    }
    const isDragStarting =
      this.props.isDragOccurring && !prevProps.isDragOccurring;
    if (!isDragStarting) {
      return null;
    }
    const { width, height } = this.ref.getBoundingClientRect();
    const snapshot = {
      width,
      height,
    };
    return snapshot;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const ref = this.ref;
    if (!ref) {
      return;
    }
    if (snapshot) {
      if (ref.style.width === snapshot.width) {
        return;
      }
      ref.style.width = `${snapshot.width}px`;
      ref.style.height = `${snapshot.height}px`;
      return;
    }
    if (this.props.isDragOccurring) {
      return;
    }
    // inline styles not applied
    if (ref.style.width == null) {
      return;
    }
    // no snapshot and drag is finished - clear the inline styles
    ref.style.removeProperty("height");
    ref.style.removeProperty("width");
  }

  setRef = (ref) => {
    this.ref = ref;
  };

  render() {
    return (
      <TableCell
        style={{ paddingTop: 6, paddingBottom: 6 }}
        ref={this.setRef}
        align={this.props.align}
      >
        {this.props.children}
      </TableCell>
    );
  }
}

const DraggableTableRow = ({ snapshot, data, row, provided, className }) => {
  return (
    <IsDraggingContext.Consumer>
      {(isDragging) => (
        <TableRow
          className={className}
          ref={provided.innerRef}
          isDragging={snapshot.isDragging}
          {...provided.draggableProps}
        >
          {row.cells.map((cell) => {
            if (cell.column.id !== "dragHandle") {
              return (
                <DraggableTableCell isDragOccurring={isDragging} scope="row">
                  {cell.render("Cell")}
                </DraggableTableCell>
              );
            } else {
              return null;
            }
          })}
          <TableCell
            style={{ paddingTop: 6, paddingBottom: 6, color: "#CCCCCC" }}
            {...provided.dragHandleProps}
          >
            <DragHandleIcon />
          </TableCell>
        </TableRow>
      )}
    </IsDraggingContext.Consumer>
  );
};

/** DRAG N DROP PREPARATION ENDS **/

const StatusFilter = styled.ul`
  list-style: none;
  display: inline-flex;
  padding: 0;

  li {
    button {
      border: 0px;
      background: transparent;
      color: #306f9d;
      font-size: 13px;
      line-height: 15px;
      padding: 0 10px;
      cursor: pointer;

      &.active {
        font-weight: bold;
        color: #333333;
      }
    }

    &:not(:last-of-type) {
      button {
        border-right: 1px solid black;
      }
    }

    &:first-of-type {
      button {
        padding-left: 0;
      }
    }
  }
`;

const statuses = Object.freeze({
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
});

export default function AwardListingPage(props) {
  const { match, disabled } = props;
  const classes = useStyles();
  const [status, setStatus] = React.useState(null);
  const [actionType, setActionType] = React.useState("");
  const [keyword, setKeyword] = React.useState("");
  const [queryString, setQueryString] = React.useState("");
  const [data, setData] = React.useState([]);
  const [totalPages, setTotalPages] = React.useState(1);
  const [selectedDate, handleDateChange] = React.useState(null);
  const { setLoading } = useLoading();
  const [categories, setCategories] = React.useState([]);

  // for pagination
  const [paginationHandler] = React.useState(new PaginationHandler());
  const [allCounts, setAllCounts] = React.useState(0);
  const [inactiveCounts, setInactiveCounts] = React.useState(0);
  const [displayAll, setDisplayAll] = React.useState(false);
  const [totalElementsByStatus, setTotalElementsByStatus] = React.useState(0);

  // for Drag & Drop
  const [orderingDisabled, setOrderingDisabled] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const onBeforeDragStart = () => {
    setIsDragging(true);
  };
  const onDragEnd = (result) => {
    //callback passed to dragdrop context
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    const items = reorder(data, result.source.index, result.destination.index);
    console.log("ON DRAG END:", items);
    setData(items);
  };

  const CustomLink = React.useMemo(
    () =>
      React.forwardRef((linkProps, ref) => <Link ref={ref} {...linkProps} />),
    []
  );

  const statusFilterHandler = (targetStatus) => () => {
    setLoading(true);
    setStatus(targetStatus);
    let target;
    if (targetStatus === statuses.ACTIVE) {
      target = "ACTIVE";
      setTotalElementsByStatus(allCounts - inactiveCounts);
    } else if (targetStatus === statuses.INACTIVE) {
      target = "INACTIVE";
      setTotalElementsByStatus(inactiveCounts);
    } else {
      target = null;
      setTotalElementsByStatus(allCounts);
    }
    paginationHandler
      .resetRows()
      .setQueryStatus("status", target)
      .filterRowsByAll();
    setData(paginationHandler.getRows());
    gotoPage(0);
    setLoading(false);
  };

  const deleteAward = async (id) => {
    const decision = await modal.fire({
      title: "Delete award",
      text: "Are you sure to delete this award?",
      showCancelButton: true,
      confirmButtonText: `Delete`,
    });
    if (decision.isConfirmed) {
      try {
        const result = await apiHelper.hkiff.deleteAward(id);
        if (result.data.code === 0) {
          toast.dark("Award deleted.");
          getAward();
        }
      } catch (err) {
        console.log(err);
      }
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Name",
        accessor: "localizations.en.name",
        Cell: ({ row }) => {
          return (
            <Link
              to={`${match.url}/${row.original.id}`}
              className={classes.text}
            >
              {row.original.localizations.en.name}
            </Link>
          );
        },
      },
      {
        Header: "Description",
        accessor: "localizations.en.description",
        disableGlobalFilter: true,
        Cell: ({ row }) => {
          return (
            <Link
              to={`${match.url}/${row.original.id}`}
              className={classes.text}
              dangerouslySetInnerHTML={{
                __html: row.original.localizations.en.description,
              }}
            ></Link>
          );
        },
      },
      {
        Header: "Status",
        accessor: "status",
        disableSortBy: true,
        Cell: ({ row }) => {
          switch (row.original.status) {
            default:
            case "INACTIVE":
              return <span className={classes.text}>Inactive</span>;
            case "ACTIVE":
              return <span className={classes.text}>Active</span>;
          }
        },
      },
      {
        Header: "Category",
        accessor: "categoryIds[0]",
        disableSortBy: true,
        Cell: ({ row }) => {
          return categories
            .filter((o) => row.original.categoryIds?.includes(o.id))
            .map((o) => o.localizations.en.name)
            .join(",");
        },
      },
      {
        Header: "Image",
        accessor: "image",
        disableSortBy: true,
        Cell: ({ row }) => {
          return <img style={{width: "250px", maxHeight: "150px"}} src={row.original.image} />;
        },
      },
      {
        Header: "Last Update",
        accessor: "updateTime",
        disableSortBy: true,
        disableGlobalFilter: true,
        Cell: ({ row }) => {
          return row.original.updateTime
            ? formatDatetime(row.original.updateTime)
            : "-";
        },
      },
    ],
    [totalPages, categories]
  );

  const {
    getTableProps,
    headerGroups,
    rows,
    prepareRow,
    setPageSize,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    selectedFlatRows,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      autoResetPage: false,
      pageCount: totalPages,
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        ...columns,
        {
          id: "action",
          // The header can use the table's getToggleAllRowsSelectedProps method
          // to render a checkbox
          Header: () => <div>Action</div>,
          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: ({ row }) => (
            <div style={{ display: "inline-flex" }}>
              <IconButton
                className={classes.iconBtn}
                size="small"
                component={CustomLink}
                to={`${match.url}/${row.original.id}`}
              >
                <Edit fontSize="small" />
              </IconButton>
              {!disabled && (
                <IconButton
                  className={classes.iconBtn}
                  size="small"
                  onClick={() => {
                    deleteAward(row.original.id);
                  }}
                >
                  <Delete fontSize="inherit" />
                </IconButton>
              )}
            </div>
          ),
        },
        // The drag handle can be used to drag the table's rows individually
        {
          id: "dragHandle",
          Header: () => <></>,
        },
      ]);
    }
  );

  const getAward = React.useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiHelper.hkiff.getAwardListing({
        page: pageIndex,
        pageSize: 999999,
        keyword: "",
        status,
        unpaged: true,
      });
      paginationHandler.setPageIndex(0).setRows(result.data.content);
      const content = paginationHandler.filterRowsByAll().getRows();
      const all = result.data.totalElements;
      const totalPages = paginationHandler.getTotalPages();
      setAllCounts(all);
      const active = paginationHandler
        .getStatuslessRows()
        .filter((x) => x && x.status === "ACTIVE").length;
      setInactiveCounts(all - active);
      console.log("RESULT:", result);
      setData(content);
      setTotalPages(totalPages);
      setTotalElementsByStatus(status === "ACTIVE" ? active : status === "INACTIVE" ? all - active : all);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const getAwardCategory = React.useCallback(async () => {
    try {
      const result = await apiHelper.hkiff.getAwardCategory(
        null,
        null,
        null,
        null,
        null,
        null,
        true
      );
      setCategories(result.data.content.filter((x) => x));
    } catch (err) {
      console.log(err);
    }
  }, []);

  const saveOrdering = async () => {
    try {
        const seqData = {};
        data.forEach((e, i) => {
            seqData[e.id] = 1 + i + pageIndex * pageSize;
        });
        const result = await apiHelper.hkiff.orderAward(seqData);
        if (result.data.code === 0) {
            toast.dark("Your changes is saved.");
            await getAward();
        }
    } catch (err) {
        console.log(err);
    }
  };

  const triggerSearch = React.useCallback(() => {
    setLoading(true);
    console.log("KEYWORD: " + keyword);
    paginationHandler
        .resetRows()
        .setQueryString(["localizations.en.name", 'localizations.en.description', 'localizations.en.comment'], keyword)
        .filterRowsByAll();
    setData(paginationHandler.getRows());
    const all = paginationHandler.getStatuslessRows().length;
    const active = paginationHandler
        .getStatuslessRows()
        .filter((x) => x && x.status == statuses.ACTIVE).length;
    const inactive = all - active;
    setAllCounts(all);
    setInactiveCounts(inactive);
    setTotalElementsByStatus(status === statuses.ACTIVE ? active : status === statuses.INACTIVE ? inactive : all);
    gotoPage(0);
    setLoading(false);
}, [gotoPage, keyword, paginationHandler, setLoading]);

  // disable ordering if used status filter or search
  React.useEffect(() => {
    if (keyword || status) {
      setOrderingDisabled(true);
    } else {
      setOrderingDisabled(false);
    }
  }, [keyword, status]);

  React.useEffect(() => {
    getAwardCategory();
  }, []);

  React.useEffect(() => {
    if (paginationHandler) {
      getAward();
    }
  }, [getAward, paginationHandler]);

  React.useEffect(() => {
    if (paginationHandler && allCounts != 0) {
      setLoading(true);
      if (displayAll == true) {
        setData(paginationHandler.setPageSize(allCounts).getRows());
        const totalPages = paginationHandler.getTotalPages();
        setTotalPages(totalPages);
      } else {
        // console.log(pageSize);
        setData(paginationHandler.setPageSize(pageSize).getRows());
        const totalPages = paginationHandler.getTotalPages();
        setTotalPages(totalPages);
        gotoPage(0);
      }
      setLoading(false);
    }
  }, [
    allCounts,
    displayAll,
    gotoPage,
    keyword,
    pageSize,
    paginationHandler,
    setLoading,
  ]);

  return (
    <div className={classes.content}>
      <Typography variant="h4" gutterBottom>
        Awards & Juries > Award Listing
      </Typography>
      <form>
        <StatusFilter>
          <li>
            <button
              className={status === null ? "active" : ""}
              type="button"
              onClick={statusFilterHandler(null)}
            >
              <Typography variant="p">All ({allCounts})</Typography>
            </button>
          </li>
          <li>
            <button
              className={status === statuses.ACTIVE ? "active" : ""}
              type="button"
              onClick={statusFilterHandler(statuses.ACTIVE)}
            >
              <Typography variant="p">
                Active ({allCounts - inactiveCounts})
              </Typography>
            </button>
          </li>
          <li>
            <button
              className={status === statuses.INACTIVE ? "active" : ""}
              type="button"
              onClick={statusFilterHandler(statuses.INACTIVE)}
            >
              <Typography variant="p">Inactive ({inactiveCounts})</Typography>
            </button>
          </li>
        </StatusFilter>
        <Grid
          container
          justify="space-between"
          style={{ paddingBottom: "10px" }}
        >
          <Grid container item xs={6} md={6}></Grid>
          <Grid container item xs={6} md={6}>
            <Box
              width="100%"
              component="span"
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
            >
              <Box display="flex">
                <Button
                  variant="contained"
                  color="primary"
                  type="button"
                  component={RouterLink}
                  disabled={disabled}
                  to={`${match.url}/new`}
                >
                  Add New Award
                </Button>
              </Box>
              <Box display="flex">
                <TextField
                  id="outlined-error"
                  label="Search"
                  variant="outlined"
                  margin="dense"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                />
              </Box>
              <Box display="flex">
                <Button
                  variant="outlined"
                  color="primary"
                  type="button"
                  margin="dense"
                  onClick={triggerSearch}
                >
                  Search
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Grid container xs={12}>
          <IsDraggingContext.Provider value={isDragging}>
            <TableContainer
              component={Paper}
              style={{
                boxShadow: "none",
                border: "1px solid #cccccc",
              }}
            >
              <fieldset disabled={disabled} className="fieldset">
                <Table
                  aria-label="simple table"
                  component="ul"
                  style={{ margin: "0" }}
                  {...getTableProps()}
                >
                  <colgroup>
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "25%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "5%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "5%" }} />
                    <col style={{ width: "5%" }} />
                  </colgroup>
                  <TableHead component="div">
                    {headerGroups.map((headerGroup) => (
                      <TableRow {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <TableCell
                            {...column.getHeaderProps(
                              column.getSortByToggleProps()
                            )}
                            className={`${column.className ?? ""}`}
                            style={{
                              paddingTop: 6,
                              paddingBottom: 6,
                              lineHeight: "1.1em",
                              height: "48px",
                            }}
                          >
                            <span
                              className={
                                column.disableSortBy !== true
                                  ? classes.sortableHeader
                                  : ``
                              }
                            >
                              {column.render("Header")}
                            </span>
                            {column.id !== "selection" &&
                            column.id !== "dragHandle" &&
                            column.disableSortBy !== true ? (
                              <TableSortLabel
                                active={column.isSorted}
                                // react-table has an unsorted state which is not treated here
                                direction={column.isSortedDesc ? "desc" : "asc"}
                              />
                            ) : null}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <DragDropContext
                    onBeforeDragStart={onBeforeDragStart}
                    onDragEnd={onDragEnd}
                  >
                    <Droppable droppableId="droppable">
                      {(provided, snapshot) => (
                  <TableBody
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  >
                    {rows.map((row, index) => {
                      prepareRow(row);
                      return (
                        <Draggable
                          key={row.id}
                          draggableId={row.id}
                          index={index}
                          isDragDisabled={disabled || orderingDisabled}
                        >
                          {(provided, snapshot) => (
                            <DraggableTableRow
                              key={row.id}
                              data={row.original}
                              row={row}
                              provided={provided}
                              snapshot={snapshot}
                            />
                          )}
                        </Draggable>
                      );
                    })}
                  </TableBody>
                  )}
                    </Droppable>
                  </DragDropContext>
                </Table>
              </fieldset>
            </TableContainer>
          </IsDraggingContext.Provider>
        </Grid>
        <Grid container justifyContent="space-between">
          <Grid container item xs={4}>
            <Box display="flex">
              <Button
                variant="contained"
                color="primary"
                type="button"
                disabled={disabled || orderingDisabled}
                onClick={saveOrdering}
              >
                Save Changes
              </Button>
            </Box>
          </Grid>
          <Grid
            container
            item
            xs={8}
            justifyContent="flex-end"
            style={{
              marginTop: "15px",
            }}
          >
            <div className={classes.flexRow}>
              <div></div>
              <PaginationBarForHandler
                pageSize={pageSize}
                setPageSize={setPageSize}
                setData={setData}
                setTotalPages={setTotalPages}
                paginationHandler={paginationHandler}
                gotoPage={gotoPage}
                pageIndex={pageIndex}
                canPreviousPage={canPreviousPage}
                previousPage={previousPage}
                nextPage={nextPage}
                canNextPage={canNextPage}
                elementCounts={{'all': totalElementsByStatus}}
                status={'all'}
              />
            </div>
          </Grid>
        </Grid>
      </form>
    </div>
  );
}
